import { isEqual, flatten } from 'lodash';

// Custom data of each state
const states = {
    qr_campaign: {
        state: 'spotlight',
        type: 'qr_code',
        time: 15
    },
    spotlight: {
        type: 'announcement',
        time: 15
    },
    jukin: {
        time: 180
    },
    plutotv: {
        time: 600
    },
    application: {
        time: 30
    },
    socialgrid: {
        time: 10
    },
    upshownow: {
        time: 120
    },
    socialzoom: {
        time: 10
    },
    trivia: {
        time: 600
    }
}

const SmartPlaylistService = {
    currentScript: null,

    // Average duration of states in categories
    categoryAverageDuration: {
        qr_campaigns: 45,
        spotlights: 25,
        social: 14,
        entertainment: 168,
        plugins: 50
    },

    // Percentage desired of each category in time window
    categoryDesiredPercentage: {
        qr_campaigns: 20,
        spotlights: 20,
        social: 10,
        entertainment: 30,
        plugins: 20
    },

    // Desired sequence duration
    timeWindow: 3600,

    categoryStates: {
        social: ['socialgrid', 'socialzoom'],
        entertainment: ['jukin', 'upshownow', 'plutotv', 'trivia'],
        qr_campaigns: ['qr_campaign'],
        spotlights: ['spotlight'],
        plugins: ['application'],
    },

    // Returns the amount of appearances that each category needs to accomplish the percentages
    calculateCategoryAppearances() {
        const categoryAppearances = {};

        for (const [category, percentage] of Object.entries(this.categoryDesiredPercentage)) {
            const duration = this.categoryAverageDuration[category];
            const appearances = Math.round((percentage / 100) * this.timeWindow / duration);
            categoryAppearances[category] = appearances;
        }

        return categoryAppearances;
    },

    // Returns random number between min and max 
    randomIntFromInterval(min, max) {
        return Math.floor(Math.random() * (max - min + 1) + min)
    },

    // This merge two arrays into one
    evenMerge(a, b) {
        const ratio = b.length / a.length;
        const mergedArray = [];

        while (a.length) {
            mergedArray.push(a.pop());
            // This gives a spice off randomness to the position merged
            mergedArray.push(...b.splice(-1 * this.randomIntFromInterval(Math.floor(ratio), Math.ceil(ratio))));
        }
        mergedArray.push(...b);

        return mergedArray;
    },

    // Take the number of appearances and return arrays filled with the category name.
    // The category name will appear the amount of appearances required.
    appearancesToItems(appearances) {
        const orderedCats = Object.keys(appearances)
        // Removes the categories with no appearances
        .filter(cat => appearances[cat] > 0)
        // Order the categories by amount of appearances
        .sort((a, b) => appearances[a] - appearances[b]);
        // Fill the array with category name
        return orderedCats.map((cat) => Array(appearances[cat]).fill(cat));
    },

    evenlyDistributeCategories(appearances) {
        // Converts number of appearances in array of items
        const categorySequences = this.appearancesToItems(appearances);

        // This is going to merge all the items by pairs
        while (categorySequences.length > 1) {
            const pair = categorySequences.splice(-2);
            categorySequences.push(this.evenMerge(pair[0], pair[1]));
        }

        return flatten(categorySequences);
    },

    // Generate the full sequence and info of the sequence
    generateFullCategorySequence() {
        // Get appearances for each category
        const appearances = this.calculateCategoryAppearances();

        // Generate and merge the appearances into a sequence
        const sequence = this.evenlyDistributeCategories({ ...appearances });

        // Calculate the total duration for each category based on the appearances and the average duration
        const timeTotal = Object.entries(appearances).reduce((acc, [cat, count]) => {
            acc[cat] = count * this.categoryAverageDuration[cat];
            return acc;
        }, {});


        return {
            sequence,
            appearances,
            timeTotal,
            sequenceTotal: Object.values(timeTotal).reduce((sum, time) => sum + time, 0) //Total time of the sequence
        };
    },

    // Takes a sequence of state names and return a valid ui_script sequence.
    buildScriptFromSequence (sequence) {
        const script = [];
        const lastUsedSubCategoryIndex = {}; // To alternate categoryStates with more than 1 state
  
        sequence.forEach((category, index) => {
            // By default the first state
            let state = this.categoryStates[category][0];

            // If the category has more than 1 state we shuffle them
            if (this.categoryStates[category].length > 1) {
                if (lastUsedSubCategoryIndex[category] === undefined) {
                    lastUsedSubCategoryIndex[category] = -1;
                }
                
                // This will iterate over all the states inside a category
                const nextIndex = (lastUsedSubCategoryIndex[category] + 1) % this.categoryStates[category].length;
                lastUsedSubCategoryIndex[category] = nextIndex;
    
                state = this.categoryStates[category][nextIndex];
            }
            
            // Get the state Info to add in the script
            const stateInfo = states[state];
            
            script.push({
                state: state,
                type: null,
                i: index,
                ...stateInfo
            });
        });

        return script;
    },

    // Main function to get a smart script
    getSmartScript(newCategoryDesiredPercentage) {

        // If they ask for the same percentage that is already generated
        if (this.currentScript && (!newCategoryDesiredPercentage || isEqual(newCategoryDesiredPercentage, this.categoryDesiredPercentage))) return this.currentScript;

        // New percentages loaded
        if (newCategoryDesiredPercentage) this.categoryDesiredPercentage = newCategoryDesiredPercentage;
       
        // Generate the sequence
        const result = this.generateFullCategorySequence();
        // Updates current script
        this.currentScript = this.buildScriptFromSequence(result.sequence);

        return this.currentScript;
    }
};

export default SmartPlaylistService;